import React, { useState, useEffect } from "react";
import burgerImg from "../assets/promo_chicken_burger.png";
import chiliImg from "../assets/ads/bannerleft.png";
import saladImg from "../assets/special_offer_salad_corner.png";

// Custom premium SVG for the golden clover logo
const CloverLogo = () => (
  <svg
    viewBox="0 0 100 100"
    className="w-12 h-12 text-[#e4b54e] fill-none animate-spin-slow opacity-80"
    stroke="currentColor"
    strokeWidth="4"
  >
    <path d="M 50 50 C 30 30, 30 10, 50 10 C 70 10, 70 30, 50 50" />
    <path d="M 50 50 C 70 30, 90 30, 90 50 C 90 70, 70 70, 50 50" />
    <path d="M 50 50 C 70 70, 70 90, 50 90 C 30 90, 30 70, 50 50" />
    <path d="M 50 50 C 30 70, 10 70, 10 50 C 10 30, 30 30, 50 50" />
  </svg>
);

// Custom premium SVG for the cloche icon
const ClocheIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 text-[#f25e1c]"
  >
    <path d="M12 4v2" />
    <circle cx="12" cy="4" r="1" fill="currentColor" />
    <path d="M4 14c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    <path d="M2 15h20v2H2z" />
  </svg>
);

const SpecialOffer = () => {
  const getTargetTime = () => {
    const now = new Date().getTime();
    // Offer ends in 3 days, 5 hours, 12 mins for authentic look
    return now + 3 * 24 * 60 * 60 * 1000 + 5 * 60 * 60 * 1000 + 12 * 60 * 1000;
  };

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Dynamic progress indicator (82% claimed)
  const [claimedPercentage, setClaimedPercentage] = useState(82);

  useEffect(() => {
    const target = getTargetTime();

    const calculateTime = () => {
      const difference = target - new Date().getTime();
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);

    // Slowly increase claimed percentage occasionally
    const progressInterval = setInterval(() => {
      setClaimedPercentage((prev) => (prev < 97 ? prev + 1 : prev));
    }, 45000);

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, []);

  const formatNum = (num) => String(num).padStart(2, "0");

  return (
    <section className="relative w-full overflow-hidden select-none bg-[#070707] text-white py-20 md:py-28 px-6 md:px-12 flex flex-col items-center justify-center">
      {/* Self-contained styling overrides for maximum premium visual effects */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float-burger {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(1deg); }
        }
        @keyframes float-chili {
          0%, 100% { transform: translateY(0) rotate(-22deg) scale(1); }
          50% { transform: translateY(-8px) rotate(-16deg) scale(1.03); }
        }
        @keyframes soft-pulse {
          0%, 100% { box-shadow: 0 0 30px rgba(242, 94, 28, 0.15); }
          50% { box-shadow: 0 0 50px rgba(242, 94, 28, 0.28); }
        }
        .animate-spin-slow {
          animation: spin-slow 22s infinite linear;
        }
        .animate-float-burger {
          animation: float-burger 6s infinite ease-in-out;
        }
        .animate-float-chili {
          animation: float-chili 4.5s infinite ease-in-out;
        }
        .animate-soft-pulse {
          animation: soft-pulse 4s infinite ease-in-out;
        }
        .rest-special-glow-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px dashed rgba(242, 94, 28, 0.25);
          animation: spin-slow 40s infinite linear;
        }
        .rest-special-glow-ring.rev {
          border: 1px solid rgba(255, 255, 255, 0.05);
          animation: spin-slow 25s infinite linear reverse;
        }
      `,
        }}
      />

      {/* Textured & Glowing Radial Backgrounds */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_50%,#1f1107_0%,#070707_75%)] pointer-events-none z-0" />
      <div className="absolute top-[20%] right-[10%] w-[350px] h-[350px] bg-[#cd3a27]/[0.03] rounded-full blur-[100px] pointer-events-none z-0" />

      {/* Elegant Plank Divider Line */}
      <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-[#f25e1c]/20 to-transparent z-10" />

      {/* Branding Logo (top-left of the section) */}
      <div className="absolute top-6 left-6 md:top-8 md:left-12 z-25">
        <CloverLogo />
      </div>

      {/* Grid Container */}
      <div className="relative max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center z-20">
        {/* Left Side: Burger Showcase with Concentric Glowing Rings */}
        <div className="lg:col-span-6 flex justify-center items-center relative min-h-[360px] md:min-h-[460px]">
          {/* Glowing Backdrops */}
          <div className="absolute w-[320px] h-[320px] md:w-[420px] md:h-[420px] bg-gradient-to-tr from-[#f25e1c]/10 to-transparent rounded-full blur-3xl pointer-events-none" />

          {/* Dashed Ring 1 */}
          <div className="rest-special-glow-ring w-[280px] h-[280px] md:w-[380px] md:h-[380px]" />

          {/* Dashed Ring 2 */}
          <div className="rest-special-glow-ring rev w-[220px] h-[220px] md:w-[300px] md:h-[300px]" />

          {/* Arched Border Dome Frame */}
          <div className="relative flex items-center justify-center border-2 border-[#f25e1c]/40 rounded-t-[12rem] md:rounded-t-[15rem] w-[250px] h-[330px] md:w-[310px] md:h-[410px] bg-black/35 shadow-[0_0_40px_rgba(242,94,28,0.1)] backdrop-blur-xs group">
            {/* Center Burger */}
            <img
              src={burgerImg}
              alt="Signature Burger"
              className="absolute bottom-6 w-[95%] transition-transform duration-700 ease-out group-hover:scale-[1.05] group-hover:rotate-[2deg] drop-shadow-[0_20px_35px_rgba(0,0,0,0.85)] animate-float-burger"
            />

            {/* Floating Chili Pepper */}
            <img
              src={chiliImg}
              alt="Hot Chili"
              className="absolute -bottom-4 -left-8 w-24 md:w-32 rotate-[-22deg] drop-shadow-[0_10px_15px_rgba(0,0,0,0.5)] z-20 animate-float-chili"
            />

            {/* Dynamic handwriting badge */}
            <div className="absolute top-12 -right-8 bg-[#e4b54e] text-neutral-900 font-mono text-[9px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg transform rotate-12 select-none border border-black/10">
              🔥 Chef's Choice
            </div>
          </div>
        </div>

        {/* Right Side: Redesigned Glassmorphic Countdown Card */}
        <div className="lg:col-span-6 flex justify-center lg:justify-start items-center">
          {/* Glass Card Container */}
          <div className="bg-[#141414]/80 border border-white/[0.06] rounded-xl p-8 md:p-10 max-w-md w-full shadow-2xl backdrop-blur-xl relative z-20 animate-soft-pulse">
            {/* Badge Indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <ClocheIcon />
              <span className="text-[#f25e1c] font-black tracking-[0.25em] text-[10px] md:text-xs uppercase font-mono">
                LIMITED TIME OFFER
              </span>
              <ClocheIcon />
            </div>

            {/* Offer Heading */}
            <h2 className="text-3xl md:text-4xl font-extrabold text-white text-center mb-2 leading-tight tracking-tight font-sans">
              30% OFF ALL ITEMS
            </h2>

            {/* Soft description */}
            <p className="text-center text-xs text-neutral-400 mb-6 max-w-xs mx-auto">
              Grab your crispy flakesfriedchickens and signature chicken buckets
              at a special discount. Only valid for online orders.
            </p>

            {/* Countdown Grid (Glassmorphism countdown items) */}
            <div className="grid grid-cols-4 gap-3 md:gap-4 mb-6">
              {/* Days */}
              <div className="flex flex-col items-center">
                <div className="bg-white/[0.04] border border-white/[0.08] text-white text-3xl font-black px-2 py-3 rounded-md w-full text-center font-mono leading-none tracking-tight shadow-inner">
                  {formatNum(timeLeft.days)}
                </div>
                <span className="text-[9px] text-neutral-400 font-bold uppercase tracking-wider mt-2 font-mono">
                  Days
                </span>
              </div>

              {/* Hours */}
              <div className="flex flex-col items-center">
                <div className="bg-white/[0.04] border border-white/[0.08] text-white text-3xl font-black px-2 py-3 rounded-md w-full text-center font-mono leading-none tracking-tight shadow-inner">
                  {formatNum(timeLeft.hours)}
                </div>
                <span className="text-[9px] text-neutral-400 font-bold uppercase tracking-wider mt-2 font-mono">
                  Hrs
                </span>
              </div>

              {/* Minutes */}
              <div className="flex flex-col items-center">
                <div className="bg-white/[0.04] border border-white/[0.08] text-white text-3xl font-black px-2 py-3 rounded-md w-full text-center font-mono leading-none tracking-tight shadow-inner">
                  {formatNum(timeLeft.minutes)}
                </div>
                <span className="text-[9px] text-neutral-400 font-bold uppercase tracking-wider mt-2 font-mono">
                  Mins
                </span>
              </div>

              {/* Seconds */}
              <div className="flex flex-col items-center">
                <div className="bg-[#f25e1c]/10 border border-[#f25e1c]/30 text-[#f25e1c] text-3xl font-black px-2 py-3 rounded-md w-full text-center font-mono leading-none tracking-tight shadow-sm">
                  {formatNum(timeLeft.seconds)}
                </div>
                <span className="text-[9px] text-[#f25e1c] font-black uppercase tracking-wider mt-2 font-mono">
                  Secs
                </span>
              </div>
            </div>

            {/* Live Progress Bar (Urgency indicator) */}
            <div className="mb-8 bg-black/30 border border-white/[0.04] p-4 rounded-lg">
              <div className="flex justify-between items-center text-[10px] md:text-xs font-mono font-bold mb-2">
                <span className="text-[#e4b54e] animate-pulse">
                  🔥 HURRY UP!
                </span>
                <span className="text-neutral-400">
                  {claimedPercentage}% Claimed
                </span>
              </div>
              <div className="w-full bg-neutral-800 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-[#f25e1c] to-[#cd3a27] h-full rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(242,94,28,0.4)]"
                  style={{ width: `${claimedPercentage}%` }}
                />
              </div>
            </div>

            {/* Order Now Button with exact matching style */}
            <div className="flex justify-center">
              <a
                href="#menu"
                className="inline-flex items-center bg-[#cd3a27] hover:bg-[#b02f1e] text-white font-sans text-[11px] font-black tracking-widest h-12 pl-6 rounded transition-all duration-300 overflow-hidden shadow-lg hover:shadow-[#cd3a27]/15 group/btn select-none"
              >
                <span className="mr-5 uppercase">CLAIM OFFER NOW</span>
                <div className="bg-white text-[#cd3a27] w-12 h-12 flex items-center justify-center transition-colors duration-300 group-hover/btn:bg-[#f25e1c] group-hover/btn:text-white border-l border-white/20">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className="w-3.5 h-3.5"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Salad Plate Corner Shot Overlay (Set on pure black background, blending seamlessly) */}
      <div className="absolute -bottom-16 -right-16 w-44 md:w-56 lg:w-72 pointer-events-none select-none z-15 opacity-85">
        <img
          src={saladImg}
          alt="Fresh Salad plate corner decoration"
          className="w-full h-auto object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.65)]"
        />
      </div>
    </section>
  );
};

export default SpecialOffer;
